import { EVA_CONFIG } from 'config/eva';
import { useEffect } from 'react';

const WIDGET_ID = 'convai-widget';
const WIDGET_SCRIPT_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
const WIDGET_AGENT_ID = 'agent_01k0f6kcppfsesdevt1h7zgshh';

const ensureWidgetElement = (): HTMLElement | null => {
  const existing = document.getElementById(WIDGET_ID);
  if (existing) return existing;

  const widget = document.createElement('elevenlabs-convai');
  widget.setAttribute('id', WIDGET_ID);
  widget.setAttribute('agent-id', WIDGET_AGENT_ID);
  widget.setAttribute('variant', 'full');
  document.body.appendChild(widget);
  return widget;
};

const ensureWidgetScript = (): HTMLScriptElement | null => {
  const existing = document.querySelector(`script[src="${WIDGET_SCRIPT_SRC}"]`);
  if (existing && existing instanceof HTMLScriptElement) return existing;

  const script = document.createElement('script');
  script.src = WIDGET_SCRIPT_SRC;
  script.async = true;
  script.type = 'text/javascript';
  document.head.appendChild(script);
  return script;
};

export default function ElevenLabsWidgetLoader(): JSX.Element | null {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const widget = ensureWidgetElement();
    ensureWidgetScript();

    if (!widget) return;

    let lastPath = window.location.pathname;
    let scrollTimeout: number | undefined;

    const shouldExcludeWidget = () =>
      EVA_CONFIG.isRouteExcluded(window.location.pathname);

    const updateWidgetVisibility = () => {
      widget.style.display = shouldExcludeWidget() ? 'none' : '';
    };

    const updateWidgetContext = () => {
      try {
        const pageContext = {
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
        };
        widget.setAttribute('dynamic-variables', JSON.stringify(pageContext));
      } catch {
        // ignore context update failures
      }
    };

    const updateWidgetVariant = () => {
      if (shouldExcludeWidget()) return;

      if (scrollTimeout) {
        window.clearTimeout(scrollTimeout);
      }

      scrollTimeout = window.setTimeout(() => {
        const scrollThreshold = 150;
        if (window.scrollY > scrollThreshold) {
          widget.setAttribute('variant', 'tiny');
        } else {
          widget.setAttribute('variant', 'full');
        }
      }, 50);
    };

    const updateContextAndVisibility = () => {
      updateWidgetContext();
      updateWidgetVisibility();
    };

    updateContextAndVisibility();
    updateWidgetVariant();

    const pathWatcher = window.setInterval(() => {
      if (window.location.pathname !== lastPath) {
        lastPath = window.location.pathname;
        updateContextAndVisibility();
        updateWidgetVariant();
      }
    }, 500);

    const handlePopState = () => {
      updateContextAndVisibility();
      updateWidgetVariant();
    };

    window.addEventListener('scroll', updateWidgetVariant, { passive: true });
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.clearInterval(pathWatcher);
      if (scrollTimeout) {
        window.clearTimeout(scrollTimeout);
      }
      window.removeEventListener('scroll', updateWidgetVariant);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return null;
}
